// src/pages/TrainingCalendar.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

/**
 * TrainingCalendar.jsx
 * Hybrid Zoho + Keka modal style (M3)
 *
 * - Single file component (drop into src/pages)
 * - LocalStorage-backed events (key: hr_training_events_v2)
 * - Month / Week / List quick toggles
 * - Filters (mode, location, search)
 * - Create/Edit modal (compact, top offset so header isn't covered)
 * - Import / Export
 *
 * Notes:
 * - Tailwind classes are used (as in your other pages). If not using Tailwind,
 *   adapt the classes to your CSS system.
 * - The calendar here is a simple month-grid implementation (fast & no external deps).
 */

/* ---------------- font import & small style ---------------- */
const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&family=Roboto:wght@300;400;500;700&display=swap');`;
const LS_KEY = "hr_training_events_v2";

const uid = (p = "id") => `${p}_${Date.now().toString(36)}_${Math.floor(Math.random() * 9999)}`;

/* ---------------- Modal (HyBrid M3) ----------------
   Compact, top-offset portal so it's visually below header.
*/
const Modal = ({ open, onClose, title, width = "max-w-3xl", children }) => {
  if (!open) return null;
  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-start justify-center pt-20 px-4"
        style={{ background: "rgba(12, 14, 20, 0.35)" }}
      >
        <motion.div
          initial={{ y: -8, scale: 0.995 }}
          animate={{ y: 0, scale: 1 }}
          exit={{ y: -8, scale: 0.995 }}
          className={`bg-white rounded-2xl shadow-2xl w-full ${width} max-h-[72vh] overflow-auto`}
        >
          <div className="sticky top-0 z-10 bg-white border-b px-5 py-3 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins, Roboto, sans-serif" }}>{title}</h3>
            </div>
            <div>
              <button onClick={onClose} className="px-3 py-1 rounded hover:bg-slate-100 text-slate-600">✕</button>
            </div>
          </div>
          <div className="p-5">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

/* ---------------- small utilities ---------------- */
const startOfMonth = (d) => {
  const dt = new Date(d.getFullYear(), d.getMonth(), 1);
  dt.setHours(0, 0, 0, 0);
  return dt;
};
const endOfMonth = (d) => {
  const dt = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  dt.setHours(23, 59, 59, 999);
  return dt;
};
const addDays = (d, n) => {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n);
  return dt;
};
const sameDay = (a, b) => {
  if (!a || !b) return false;
  const A = new Date(a);
  const B = new Date(b);
  return A.getFullYear() === B.getFullYear() && A.getMonth() === B.getMonth() && A.getDate() === B.getDate();
};
const formatDateShort = (d) => {
  const D = new Date(d);
  return D.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
};

/* ---------------- sample mock events ---------------- */
const SAMPLE_EVENTS = [
  {
    id: uid("t"),
    title: "Sales Excellence — Module 1",
    start: new Date().toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
    mode: "Offline",
    location: "Conference Room A",
    trainer: "Ramesh Gupta",
    seats: 25,
    description: "First session of sales excellence program.",
    color: "#7c3aed", // violet
  },
  {
    id: uid("t"),
    title: "Leadership Workshop",
    start: addDays(new Date(), 4).toISOString().slice(0, 10),
    end: addDays(new Date(), 4).toISOString().slice(0, 10),
    mode: "Online",
    location: "Zoom",
    trainer: "Priya Singh",
    seats: 120,
    description: "Cross-functional leadership workshop.",
    color: "#059669", // green
  },
  {
    id: uid("t"),
    title: "Excel for Business",
    start: addDays(new Date(), 10).toISOString().slice(0, 10),
    end: addDays(new Date(), 10).toISOString().slice(0, 10),
    mode: "Offline",
    location: "Training Lab",
    trainer: "Ashok Mehra",
    seats: 18,
    description: "Hands-on Excel session.",
    color: "#f97316", // orange
  },
];

/* ---------------- Main Component ---------------- */
export default function TrainingCalendar() {
  // persistent events
  const [events, setEvents] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) {
        localStorage.setItem(LS_KEY, JSON.stringify(SAMPLE_EVENTS));
        return SAMPLE_EVENTS;
      }
      return JSON.parse(raw);
    } catch {
      return SAMPLE_EVENTS;
    }
  });

  const [view, setView] = useState("month"); // month | week | list
  const [cursor, setCursor] = useState(new Date()); // current month/week pointer
  const [filterMode, setFilterMode] = useState("All"); // All/Online/Offline
  const [filterLocation, setFilterLocation] = useState("All");
  const [search, setSearch] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // event id or null
  const [fileRef, setFileRef] = useState(null);
  const inputFileRef = useRef(null);

  // persist events to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(events));
    } catch (e) {
      // ignore
    }
  }, [events]);

  // derived values
  const locations = useMemo(() => {
    const setLoc = new Set(events.map((e) => e.location).filter(Boolean));
    return ["All", ...Array.from(setLoc)];
  }, [events]);

  const filteredEvents = useMemo(() => {
    return events.filter((ev) => {
      if (filterMode !== "All" && ev.mode !== filterMode) return false;
      if (filterLocation !== "All" && ev.location !== filterLocation) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        if (!(`${ev.title} ${ev.trainer} ${ev.description} ${ev.location}`.toLowerCase().includes(s))) return false;
      }
      return true;
    });
  }, [events, filterMode, filterLocation, search]);

  // calendar grid for month
  const monthGrid = useMemo(() => {
    const start = startOfMonth(cursor);
    const firstDay = start.getDay(); // 0..6 Sun..Sat
    const startGrid = addDays(start, -firstDay);
    const weeks = [];
    for (let w = 0; w < 6; w++) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        const dt = addDays(startGrid, w * 7 + d);
        const dayEvents = filteredEvents.filter((ev) => {
          const evStart = new Date(ev.start);
          const evEnd = new Date(ev.end || ev.start);
          evStart.setHours(0, 0, 0, 0);
          evEnd.setHours(23, 59, 59, 999);
          const inRange = dt >= evStart && dt <= evEnd;
          return inRange;
        });
        week.push({ date: dt, events: dayEvents });
      }
      weeks.push(week);
    }
    return weeks;
  }, [cursor, filteredEvents]);

  // week view grid
  const weekGrid = useMemo(() => {
    const start = addDays(cursor, -cursor.getDay());
    const days = [];
    for (let i = 0; i < 7; i++) {
      const dt = addDays(start, i);
      const dayEvents = filteredEvents.filter((ev) => {
        const evStart = new Date(ev.start);
        const evEnd = new Date(ev.end || ev.start);
        evStart.setHours(0, 0, 0, 0);
        evEnd.setHours(23, 59, 59, 999);
        return dt >= evStart && dt <= evEnd;
      });
      days.push({ date: dt, events: dayEvents });
    }
    return days;
  }, [cursor, filteredEvents]);

  const listEvents = useMemo(() => {
    const sorted = [...filteredEvents].sort((a, b) => new Date(a.start) - new Date(b.start));
    return sorted;
  }, [filteredEvents]);

  /* ---------------- Calendar navigation ---------------- */
  const gotoToday = () => setCursor(new Date());
  const prev = () => {
    const c = new Date(cursor);
    if (view === "month") c.setMonth(c.getMonth() - 1);
    else c.setDate(c.getDate() - 7);
    setCursor(c);
  };
  const next = () => {
    const c = new Date(cursor);
    if (view === "month") c.setMonth(c.getMonth() + 1);
    else c.setDate(c.getDate() + 7);
    setCursor(c);
  };

  /* ---------------- Create / Edit / Delete ---------------- */
  const openCreateModal = (initial = null) => {
    setEditing(initial || {
      id: uid("t"),
      title: "",
      start: new Date().toISOString().slice(0, 10),
      end: new Date().toISOString().slice(0, 10),
      mode: "Offline",
      location: "",
      trainer: "",
      seats: 20,
      description: "",
      color: "#6366f1",
    });
    setModalOpen(true);
  };

  const openEditModal = (ev) => {
    setEditing({ ...ev });
    setModalOpen(true);
  };

  const saveEvent = (payload) => {
    setEvents((p) => {
      const idx = p.findIndex((x) => x.id === payload.id);
      if (idx === -1) return [payload, ...p];
      const cp = [...p];
      cp[idx] = payload;
      return cp;
    });
    setModalOpen(false);
    setEditing(null);
  };

  const removeEvent = (id) => {
    if (!window.confirm("Delete training event?")) return;
    setEvents((p) => p.filter((x) => x.id !== id));
    setModalOpen(false);
    setEditing(null);
  };

  /* ---------------- Import / Export ---------------- */
  const exportAll = () => {
    const blob = new Blob([JSON.stringify(events, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `training_events_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        const arr = Array.isArray(parsed) ? parsed : [parsed];
        const normalized = arr.map((it) => ({ ...it, id: it.id || uid("t") }));
        setEvents((p) => [...normalized, ...p]);
        alert("Imported trainings");
      } catch (err) {
        alert("Import failed: invalid JSON");
      }
    };
    reader.readAsText(file);
  };

  /* ---------------- small helpers for UI ---------------- */
  const eventBadge = (ev) => (
    <div
      className="text-xs px-2 py-0.5 rounded-full text-white truncate"
      style={{ background: ev.color || "#6b7280", maxWidth: "130px" }}
      title={`${ev.title} — ${ev.trainer}`}
    >
      {ev.title}
      <span className="ml-2 text-[10px] opacity-80"> {new Date(ev.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
    </div>
  );

  /* ---------------- small UI components ---------------- */

  const Controls = () => (
    <div className="flex items-center justify-between gap-4 w-full">
      <div className="flex items-center gap-3">
        <button onClick={prev} className="px-3 py-2 rounded-md bg-white border hover:bg-slate-50 text-sm">Prev</button>
        <button onClick={gotoToday} className="px-3 py-2 rounded-md bg-white border hover:bg-slate-50 text-sm">Today</button>
        <button onClick={next} className="px-3 py-2 rounded-md bg-white border hover:bg-slate-50 text-sm">Next</button>
        <div className="px-3 py-2 rounded-md bg-white border text-sm font-medium">{cursor.toLocaleString("en-IN", { month: "long", year: "numeric" })}</div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => setView("month")} className={`px-3 py-2 rounded-md text-sm ${view === "month" ? "bg-indigo-600 text-white" : "bg-white border"}`}>Month</button>
        <button onClick={() => setView("week")} className={`px-3 py-2 rounded-md text-sm ${view === "week" ? "bg-indigo-600 text-white" : "bg-white border"}`}>Week</button>
        <button onClick={() => setView("list")} className={`px-3 py-2 rounded-md text-sm ${view === "list" ? "bg-indigo-600 text-white" : "bg-white border"}`}>List</button>
      </div>
    </div>
  );

  /* ---------------- rendering ---------------- */
  return (
    <div className="min-h-screen bg-slate-50 p-6" style={{ fontFamily: "Poppins, Roboto, sans-serif" }}>
      <style>{FONT_IMPORT}</style>

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Training Calendar</h1>
            <p className="text-sm text-slate-500 mt-1">Plan, schedule and manage all corporate trainings. Filters, analytics, attendee preview and import/export supported.</p>
          </div>

          <div className="flex items-center gap-3">
            <div className="px-3 py-2 rounded-lg bg-white border shadow-sm text-sm hover:shadow-md cursor-pointer" onClick={() => openCreateModal()}>
              <div className="flex items-center gap-2 text-indigo-700 font-semibold">
                <span className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center">+</span>
                <div>Create Training</div>
              </div>
            </div>
            <button onClick={exportAll} className="px-4 py-2 rounded-lg bg-white border hover:bg-slate-50 text-sm">Export</button>
            <label className="px-4 py-2 rounded-lg bg-white border hover:bg-slate-50 text-sm cursor-pointer">
              Import
              <input ref={inputFileRef} type="file" accept="application/json" onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0])} className="hidden" />
            </label>
            <button onClick={() => { localStorage.removeItem(LS_KEY); setEvents(SAMPLE_EVENTS); alert("Reset to sample events"); }} className="px-4 py-2 rounded-lg bg-white border hover:bg-slate-50 text-sm">Reset</button>
          </div>
        </header>

        {/* Top badges + filters */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-full px-3 py-1 text-sm border">Total <span className="font-semibold ml-2">{events.length}</span></div>
            <div className="bg-white rounded-full px-3 py-1 text-sm border">Online <span className="font-semibold ml-2">{events.filter(e => e.mode === "Online").length}</span></div>
            <div className="bg-white rounded-full px-3 py-1 text-sm border">Offline <span className="font-semibold ml-2">{events.filter(e => e.mode === "Offline").length}</span></div>
          </div>

          <div className="flex items-center gap-3">
            <div className="bg-white border rounded-lg flex items-center px-3 py-2 gap-2">
              <select value={filterMode} onChange={(e) => setFilterMode(e.target.value)} className="bg-transparent outline-none text-sm">
                <option>All</option>
                <option>Online</option>
                <option>Offline</option>
              </select>
            </div>

            <div className="bg-white border rounded-lg flex items-center px-3 py-2 gap-2">
              <select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)} className="bg-transparent outline-none text-sm">
                {locations.map((l) => <option key={l}>{l}</option>)}
              </select>
            </div>

            <div className="bg-white border rounded-lg px-3 py-2">
              <input placeholder="Search trainings, trainer, location" value={search} onChange={(e) => setSearch(e.target.value)} className="outline-none text-sm w-64" />
            </div>
          </div>
        </div>

        {/* Controls */}
        <Controls />

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* left: filters & quick */}
          <aside className="lg:col-span-1 bg-white p-4 rounded-2xl shadow-sm h-[64vh] overflow-auto">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-slate-800">Filters</h3>
              <p className="text-xs text-slate-500 mt-1">Refine the calendar view</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-slate-600">Mode</label>
                <select value={filterMode} onChange={(e) => setFilterMode(e.target.value)} className="w-full px-3 py-2 border rounded mt-1">
                  <option>All</option>
                  <option>Online</option>
                  <option>Offline</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-slate-600">Location</label>
                <select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)} className="w-full px-3 py-2 border rounded mt-1">
                  {locations.map((l) => <option key={l}>{l}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs text-slate-600">Quick Range</label>
                <div className="flex gap-2 mt-1">
                  <button onClick={() => { setCursor(new Date()); setView("month"); }} className="flex-1 px-3 py-2 rounded bg-white border text-sm">This Month</button>
                  <button onClick={() => { setCursor(new Date()); setView("week"); }} className="flex-1 px-3 py-2 rounded bg-white border text-sm">This Week</button>
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-600">Quick Filters</label>
                <div className="flex gap-2 mt-2">
                  <button className="px-3 py-2 rounded bg-indigo-50 text-indigo-700 text-sm">Upcoming</button>
                  <button className="px-3 py-2 rounded bg-emerald-50 text-emerald-700 text-sm">Popular</button>
                </div>
              </div>

              <div className="pt-4 border-t">
                <h4 className="text-sm font-semibold">Legend</h4>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2 text-sm"><span className="w-3 h-3 rounded-full bg-violet-600" /> Technical</div>
                  <div className="flex items-center gap-2 text-sm"><span className="w-3 h-3 rounded-full bg-emerald-600" /> Leadership</div>
                  <div className="flex items-center gap-2 text-sm"><span className="w-3 h-3 rounded-full bg-orange-500" /> Functional</div>
                </div>
              </div>
            </div>
          </aside>

          {/* center: calendar */}
          <section className="lg:col-span-2 bg-white p-4 rounded-2xl shadow-lg min-h-[64vh] overflow-auto">
            {/* month grid */}
            {view === "month" && (
              <div>
                <div className="grid grid-cols-7 gap-1 text-sm border-b pb-2 mb-3">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                    <div key={d} className="text-slate-600 font-medium px-2">{d}</div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-2">
                  {monthGrid.map((week, wi) => (
                    <React.Fragment key={wi}>
                      {week.map((cell, idx) => {
                        const inMonth = cell.date.getMonth() === cursor.getMonth();
                        const isToday = sameDay(cell.date, new Date());
                        return (
                          <div
                            key={idx}
                            className={`min-h-[100px] rounded-lg p-2 ${inMonth ? "bg-white" : "bg-slate-50"} border ${isToday ? "ring-2 ring-indigo-200" : "border-transparent"}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className={`text-sm ${inMonth ? "text-slate-800" : "text-slate-400"} font-medium`}>{cell.date.getDate()}</div>
                              <div className="text-xs text-slate-400">{cell.date.toLocaleString("en-IN", { month: "short" })}</div>
                            </div>

                            <div className="mt-2 space-y-1 overflow-hidden">
                              {cell.events.slice(0, 3).map((ev) => (
                                <div key={ev.id} onClick={() => openEditModal(ev)} className="cursor-pointer" title={`${ev.title} — ${ev.trainer}`}>
                                  <div style={{ background: ev.color || "#6b7280" }} className="text-xs px-2 py-1 rounded-full text-white truncate">{ev.title}</div>
                                </div>
                              ))}

                              {cell.events.length > 3 && (
                                <div className="mt-1 text-xs text-slate-400">+{cell.events.length - 3} more</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* week view */}
            {view === "week" && (
              <div>
                <div className="grid grid-cols-7 gap-2 text-sm mb-3">
                  {weekGrid.map((wd) => (
                    <div key={wd.date.toISOString()} className="bg-slate-50 p-3 rounded-lg min-h-[220px]">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">{wd.date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</div>
                        <div className="text-xs text-slate-400">{wd.date.toLocaleDateString("en-IN", { month: "short" })}</div>
                      </div>

                      <div className="mt-3 space-y-2">
                        {wd.events.map((ev) => {
                          const calcClass = "p-2 rounded-lg cursor-pointer hover:shadow-md";
                          return (
                            <div key={ev.id} onClick={() => openEditModal(ev)} style={{ background: ev.color || "#6b7280" }} className={`${calcClass} text-white`}>
                              <div className="text-sm font-semibold">{ev.title}</div>
                              <div className="text-xs opacity-90">{ev.trainer} · {ev.location}</div>
                            </div>
                          );
                        })}
                        {wd.events.length === 0 && <div className="text-xs text-slate-400">No trainings</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* list view */}
            {view === "list" && (
              <div>
                <div className="space-y-3">
                  {listEvents.map((ev) => (
                    <div key={ev.id} className="p-3 rounded-lg bg-white border flex items-center justify-between">
                      <div>
                        <div className="text-lg font-semibold text-slate-900">{ev.title}</div>
                        <div className="text-xs text-slate-500">{formatDateShort(ev.start)} · {ev.mode} · {ev.location}</div>
                        <div className="text-xs text-slate-500 mt-1">Trainer: {ev.trainer} · Seats: {ev.seats}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEditModal(ev)} className="px-3 py-2 rounded bg-white border text-sm">Edit</button>
                        <button onClick={() => removeEvent(ev.id)} className="px-3 py-2 rounded bg-rose-50 text-rose-700 border text-sm">Delete</button>
                      </div>
                    </div>
                  ))}
                  {listEvents.length === 0 && <div className="text-center text-slate-500 p-8">No trainings in this filter range.</div>}
                </div>
              </div>
            )}
          </section>

          {/* right: analytics & quick actions */}
          <aside className="lg:col-span-1 bg-white p-4 rounded-2xl shadow-sm h-[64vh] overflow-auto">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Quick Actions</h3>
              <div className="mt-3 space-y-2">
                <button onClick={() => openCreateModal()} className="w-full px-3 py-2 rounded bg-indigo-600 text-white">Create Training</button>
                <button onClick={() => exportAll()} className="w-full px-3 py-2 rounded bg-white border">Export All</button>
                <label className="w-full block">
                  <div className="w-full px-3 py-2 rounded bg-white border text-center cursor-pointer">Import File</div>
                  <input type="file" accept="application/json" onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0])} className="hidden" />
                </label>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-sm font-semibold">Upcoming Trainings</h4>
              <div className="mt-2 space-y-2">
                {listEvents.slice(0, 6).map((ev) => (
                  <div key={ev.id} className="flex items-start gap-3">
                    <div className="w-2 h-10 rounded" style={{ background: ev.color || "#6b7280" }} />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{ev.title}</div>
                      <div className="text-xs text-slate-500">{formatDateShort(ev.start)} · {ev.mode}</div>
                    </div>
                    <div className="text-xs text-slate-500">{ev.seats}</div>
                  </div>
                ))}
                {listEvents.length === 0 && <div className="text-xs text-slate-500">No upcoming trainings</div>}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* ------------- Create / Edit Modal ------------- */}
      <Modal open={isModalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} title={editing?.id ? (editing.title || "Edit Training") : "Create Training"} width="max-w-2xl">
        {editing && (
          <TrainingForm
            initial={editing}
            onCancel={() => { setModalOpen(false); setEditing(null); }}
            onSave={(payload) => saveEvent(payload)}
            onDelete={(id) => removeEvent(id)}
            locations={locations}
          />
        )}
      </Modal>
    </div>
  );
}

/* ---------------- Training Form (used in modal) ---------------- */
function TrainingForm({ initial, onSave, onCancel, onDelete, locations = [] }) {
  const [form, setForm] = useState({ ...initial });

  // keep consistent date order
  useEffect(() => {
    if (new Date(form.end) < new Date(form.start)) {
      setForm((f) => ({ ...f, end: f.start }));
    }
  }, [form.start]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-slate-600">Title</label>
          <input value={form.title} onChange={(e) => update({ title: e.target.value })} className="w-full px-3 py-2 border rounded-md" placeholder="Training title" />
        </div>
        <div>
          <label className="block text-xs text-slate-600">Mode</label>
          <select value={form.mode} onChange={(e) => update({ mode: e.target.value })} className="w-full px-3 py-2 border rounded-md">
            <option>Offline</option>
            <option>Online</option>
          </select>
        </div>

        <div>
          <label className="block text-xs text-slate-600">Start Date</label>
          <input type="date" value={form.start} onChange={(e) => update({ start: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
        </div>
        <div>
          <label className="block text-xs text-slate-600">End Date</label>
          <input type="date" value={form.end} onChange={(e) => update({ end: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
        </div>

        <div>
          <label className="block text-xs text-slate-600">Location</label>
          <input list="locs" value={form.location} onChange={(e) => update({ location: e.target.value })} className="w-full px-3 py-2 border rounded-md" />
          <datalist id="locs">
            {locations.filter(Boolean).map((l) => <option key={l} value={l} />)}
          </datalist>
        </div>

        <div>
          <label className="block text-xs text-slate-600">Trainer</label>
          <input value={form.trainer} onChange={(e) => update({ trainer: e.target.value })} className="w-full px-3 py-2 border rounded-md" placeholder="Trainer name" />
        </div>

        <div>
          <label className="block text-xs text-slate-600">Seats</label>
          <input type="number" min={1} value={form.seats} onChange={(e) => update({ seats: Number(e.target.value) })} className="w-full px-3 py-2 border rounded-md" />
        </div>

        <div>
          <label className="block text-xs text-slate-600">Color (badge)</label>
          <input type="color" value={form.color || "#6366f1"} onChange={(e) => update({ color: e.target.value })} className="w-full px-2 py-2 border rounded-md h-10" />
        </div>
      </div>

      <div>
        <label className="block text-xs text-slate-600">Description</label>
        <textarea value={form.description} onChange={(e) => update({ description: e.target.value })} className="w-full px-3 py-2 border rounded-md h-28" placeholder="Add agenda or additional notes" />
      </div>

      <div className="flex items-center justify-between gap-3 pt-3 border-t">
        <div className="flex items-center gap-2">
          {form.id && <button onClick={() => onDelete(form.id)} className="px-3 py-2 rounded bg-rose-50 text-rose-700 border">Delete</button>}
          <button onClick={onCancel} className="px-3 py-2 rounded bg-white border">Cancel</button>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-500 mr-2">Preview</div>
          <div className="rounded px-3 py-2" style={{ background: form.color || "#6366f1", color: "#fff" }}>
            <div className="text-sm font-semibold">{form.title || "Untitled"}</div>
            <div className="text-[11px] opacity-90">{form.trainer || "-"}</div>
          </div>
          <button onClick={() => onSave({ ...form })} className="px-4 py-2 rounded bg-indigo-600 text-white">Save Training</button>
        </div>
      </div>
    </div>
  );
}
